import { Base64 } from "js-base64";
import Settings from "@2kog/pkg-common/data/settings";
import User from "@2kog/pkg-common/utils/user";
import { refreshAccountToken } from "@/service/account";

export const TOKEN_REFRESH_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const TOKEN_STORAGE_KEY = Settings.tokenKey;
const LAST_AUTH_STORAGE_KEY = "created_at";
const REFRESH_LOCK_NAME = "rx-planet-auth-token-refresh";

let refreshInFlight = null;

export function getTokenExpiresAt(token) {
    if (!token || typeof token !== "string") return 0;

    try {
        const payload = token.split(".")[1];
        if (!payload) return 0;

        const decoded = JSON.parse(Base64.decode(payload));
        const expiresAt = Number(decoded?.exp) * 1000;
        return Number.isFinite(expiresAt) ? expiresAt : 0;
    } catch (error) {
        return 0;
    }
}

export function shouldRefreshAuthToken(token, now = Date.now()) {
    const expiresAt = getTokenExpiresAt(token);
    if (!expiresAt || expiresAt <= now) return false;

    return expiresAt - now <= TOKEN_REFRESH_WINDOW_MS;
}

export function syncRefreshedAuthToken(token, now = Date.now()) {
    if (!token) return;

    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(LAST_AUTH_STORAGE_KEY, String(now));

    // pkg-common 的 User 是单例；同步其内存态，避免当前页面继续读到旧 token。
    if (User.profile) User.profile.token = token;
    User.created_at = now;
}

async function requestTokenRefreshIfNeeded() {
    // 获取锁后重新读取，另一标签页可能已经完成续签并更新了 localStorage。
    const token = User.getToken();
    if (!shouldRefreshAuthToken(token)) {
        return { refreshed: false, reason: "not-due" };
    }

    const response = await refreshAccountToken();
    const data = response?.data?.data || {};

    if (data.refreshed && data.token) {
        syncRefreshedAuthToken(data.token);
    }

    return data;
}

function runWithCrossTabLock() {
    if (typeof navigator !== "undefined" && navigator.locks?.request) {
        return navigator.locks.request(REFRESH_LOCK_NAME, requestTokenRefreshIfNeeded);
    }

    return requestTokenRefreshIfNeeded();
}

/**
 * 仅在 JWT 剩余有效期不超过 7 天时请求后端续签。
 * 后端仍负责最终判断；同一页面复用进行中的 Promise，支持 Web Locks 的浏览器还会跨标签页串行。
 */
export function refreshAuthTokenIfNeeded() {
    if (!User.isLogin()) {
        return Promise.resolve({ refreshed: false, reason: "logged-out" });
    }

    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = runWithCrossTabLock().finally(() => {
        refreshInFlight = null;
    });

    return refreshInFlight;
}
